import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { initials, inr, STATUS_LABEL } from "@/lib/format";

const PALETTE = ["#C8553D", "#2E7D32", "#D97706", "#2563EB", "#7C3AED", "#DB2777", "#0F766E"];

export function Avatar({ name = "", id = "", size = 40 }) {
  const seed = [...(id || name)].reduce((a, c) => a + c.charCodeAt(0), 0);
  const color = PALETTE[seed % PALETTE.length];
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold shrink-0"
      style={{ width: size, height: size, background: `${color}1A`, color, fontSize: size * 0.36 }}
    >
      {initials(name) || "?"}
    </div>
  );
}

export function HsId({ value, big = false, testid }) {
  const [done, setDone] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const t = document.createElement("textarea");
      t.value = value;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setDone(true);
    toast.success(`${value} copied`);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid || `hs-id-${value}`}
      className={`hs-id inline-flex items-center gap-1.5 rounded-lg bg-[#F9ECE8] text-[#B34832] font-semibold transition-colors hover:bg-[#F3DDD6] ${big ? "px-3 py-1.5 text-lg" : "px-1.5 py-0.5 text-[11px]"}`}
    >
      {value}
      {done ? <Check size={big ? 16 : 11} /> : <Copy size={big ? 16 : 11} />}
    </button>
  );
}

export function Money({ value, hidden = false, className = "", testid }) {
  return (
    <span data-testid={testid} className={`tabular-nums ${className}`}>
      {hidden ? "₹ •••••••" : inr(value)}
    </span>
  );
}

export function EyeToggle({ hidden, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="privacy-toggle-button"
      aria-label={hidden ? "Show values" : "Hide values"}
      className="w-10 h-10 rounded-full bg-white/80 border border-[#E2D9CF] flex items-center justify-center text-[#4A5568] hover:text-[#C8553D] transition-colors"
    >
      {hidden ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );
}

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700",
  edit_requested: "bg-blue-50 text-blue-700",
  confirmed: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  active: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  inactive: "bg-slate-100 text-slate-500",
};

export function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid} className={`hd-chip ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function PageHeader({ title, subtitle, right, back = true }) {
  const nav = useNavigate();
  return (
    <div className="flex items-start gap-3 pt-5 pb-4">
      {back && (
        <button
          type="button"
          data-testid="page-back-button"
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          className="w-10 h-10 -ml-1 rounded-full flex items-center justify-center hover:bg-white transition-colors shrink-0"
        >
          <ArrowLeft size={20} />
        </button>
      )}
      <div className="flex-1 min-w-0 pt-1">
        <h1 className="text-xl font-bold tracking-tight truncate" data-testid="page-title">{title}</h1>
        {subtitle && <div className="text-sm text-[#718096] mt-0.5">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

export function Section({ title, action, children, className = "" }) {
  return (
    <section className={`mt-6 ${className}`}>
      <div className="flex items-center justify-between mb-2.5">
        <h2 className="text-base font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Empty({ icon: Icon, text, testid }) {
  return (
    <div data-testid={testid} className="hd-card text-center py-8 text-sm text-[#718096]">
      {Icon && <Icon className="mx-auto mb-2 text-[#C8553D]/60" size={26} />}
      {text}
    </div>
  );
}

export function Row({ label, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
      <span className="text-sm text-[#718096] shrink-0">{label}</span>
      <span className="text-sm font-medium text-right break-words" data-testid={testid}>{children}</span>
    </div>
  );
}

export function SidesBar({ sides = [] }) {
  const colors = ["#C8553D", "#2563EB", "#D97706", "#2E7D32"];
  return (
    <div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-[#F5F0E9]">
        {sides.map((s, i) => (
          <div key={s.karta_id} style={{ width: `${Number(s.percentage)}%`, background: colors[i % 4] }} />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs">
        {sides.map((s, i) => (
          <span key={s.karta_id} className="font-semibold" style={{ color: colors[i % 4] }} data-testid={`side-pct-${s.name}`}>
            {s.name} Side {Number(s.percentage).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%
          </span>
        ))}
      </div>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex justify-center py-16" data-testid="loading-spinner">
      <div className="w-7 h-7 border-[3px] border-[#C8553D]/20 border-t-[#C8553D] rounded-full animate-spin" />
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[#718096] mt-1">{hint}</span>}
    </label>
  );
}
